import React, { useEffect, useState } from "react";

import { CreateModal } from "../../components/Modals";
import { useNotify } from "../../context/NotificationContext";
import {
  fetchStudentDocumentStatus,
  updateStudentDocumentStatus,
} from "../../services/studentService/studentService";
import type { StudentPurchase } from "../../services/studentService/studentService.types";
import { getServerErrorData } from "../../utils/httpError";
import { logger } from "../../utils/logger";

interface StudentDocumentModalProps {
  purchase: StudentPurchase;
  onClose: () => void;
}

const documentTypeOptions = [
  { label: "Сертификат", value: "certificate" },
  { label: "Удостоверение", value: "identity" },
  { label: "Диплом", value: "diploma" },
  { label: "Свидетельство", value: "certificate_other" },
];

const statusOptions = [
  { label: "Не выбран", value: "not_selected" },
  { label: "К выдаче", value: "planned" },
  { label: "Выдан", value: "issued" },
  { label: "Не выдавать", value: "not_issued" },
];

const StudentDocumentModal: React.FC<StudentDocumentModalProps> = ({ purchase, onClose }) => {
  const [initialValues, setInitialValues] = useState<Record<string, string> | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [externalErrors, setExternalErrors] = useState<Record<string, string>>({});
  const toast = useNotify();

  useEffect(() => {
    let active = true;
    fetchStudentDocumentStatus(purchase.student.id, purchase.course.id)
      .then((data) => {
        if (!active) return;
        setInitialValues({
          document_type: data.document_type || "certificate",
          status: data.status || "not_selected",
          comment: data.comment || "",
        });
      })
      .catch((error) => {
        logger.error("Ошибка загрузки статуса документа:", error);
        toast.show({ severity: "error", summary: "Ошибка", detail: "Не удалось загрузить документ по курсу", life: 3000 });
        onClose();
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [onClose, purchase.course.id, purchase.student.id, toast]);

  const submit = async (formData: Record<string, string | File[] | null>) => {
    setSaving(true);
    setExternalErrors({});
    try {
      await updateStudentDocumentStatus(purchase.student.id, purchase.course.id, {
        document_type: String(formData.document_type) as "certificate" | "identity" | "diploma" | "certificate_other",
        status: String(formData.status) as "not_selected" | "planned" | "issued" | "not_issued",
        comment: String(formData.comment || ""),
      });
      toast.show({ severity: "success", summary: "Готово", detail: "Документ по курсу сохранен", life: 3000 });
      onClose();
    } catch (error) {
      const serverErrors = getServerErrorData(error);
      if (serverErrors) {
        const formatted: Record<string, string> = {};
        Object.entries(serverErrors).forEach(([key, value]) => {
          formatted[key] = String(value);
        });
        setExternalErrors(formatted);
      } else {
        logger.error("Ошибка сохранения документа ученика:", error);
        toast.show({ severity: "error", summary: "Ошибка", detail: "Не удалось сохранить документ по курсу", life: 3000 });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading || !initialValues) return null;

  const studentName = purchase.student.full_name || purchase.student.email || "ученик";

  return (
    <CreateModal
      title={`Документ: ${studentName} - ${purchase.course.title}`}
      fields={[
        {
          name: "document_type",
          type: "selectWithInput",
          placeholder: "Тип документа",
          required: true,
          options: documentTypeOptions,
        },
        {
          name: "status",
          type: "selectWithInput",
          placeholder: "Статус",
          required: true,
          options: statusOptions,
        },
        { name: "comment", type: "textarea", placeholder: "Комментарий", required: false },
      ]}
      initialValues={initialValues}
      onSubmit={submit}
      onCancel={onClose}
      submitButtonText="Сохранить"
      externalErrors={externalErrors}
      isLoading={saving}
    />
  );
};

export default StudentDocumentModal;
